import { useEffect, useMemo, useRef } from "react";
import {
  createCategoriesStore,
  createItemsStore,
  createNotesStore,
  createTodosStore,
  createTripDetailStore,
} from "@pack-it-up/viewmodel";
import type { TripId } from "@pack-it-up/shared";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "./AppContext.js";
import { useSessionDeps } from "./sessionDeps.js";

// The page-scoped store bundle for one trip. `detail` aggregates reads + completion
// toggle + trip header edits; the entity stores own create/update/move/remove for their
// kind (Doc 01 §5 — the UI mutates through stores, never repositories directly).
export interface TripStores {
  detail: ReturnType<typeof createTripDetailStore>;
  items: ReturnType<typeof createItemsStore>;
  categories: ReturnType<typeof createCategoriesStore>;
  todos: ReturnType<typeof createTodosStore>;
  notes: ReturnType<typeof createNotesStore>;
}

// Builds the bundle and drives `detail`'s open/close lifecycle off the route's tripId.
// `open` subscribes the detail store to this trip's live streams (trip, categories,
// items, todos, notes) + derives role/canEdit; `close` tears them down. Returns null
// until the signed-in user id resolves.
export const useTripDetail = (tripId: TripId): TripStores | null => {
  const { auth } = useStores();
  const { repos } = useSessionDeps();
  const actor = useStoreValue(auth, (s) => s.user?.id ?? null);

  const stores = useMemo<TripStores | null>(() => {
    if (actor === null) return null;
    const detail = createTripDetailStore({ repos, actor });
    // The entity stores read the trip-level "pack for others" flag lazily off the
    // detail store so it's always current (T23 §2).
    const allowProxyCompletion = (): boolean => detail.getState().trip?.settings.allowProxyCompletion ?? false;
    // T27: staging is effectively on only when this actor has opted in — mirrors
    // tripDetailStore's own `stagingEnabled()`.
    const stagingEnabled = (): boolean => {
      const trip = detail.getState().trip;
      return trip?.members[actor]?.settings.stagingOptIn ?? false;
    };
    return {
      detail,
      items: createItemsStore({ items: repos.items, listId: tripId, actor, allowProxyCompletion, stagingEnabled }),
      categories: createCategoriesStore({ categories: repos.categories, parentId: tripId, actor }),
      todos: createTodosStore({ todos: repos.todos, listId: tripId, actor, allowProxyCompletion }),
      notes: createNotesStore({ notes: repos.notes, listId: tripId, actor }),
    };
  }, [repos, actor, tripId]);

  useEffect(() => {
    if (stores === null || actor === null) return;
    stores.detail.getState().open(tripId, actor);
    return () => stores.detail.getState().close();
  }, [stores, tripId, actor]);

  // The entity stores own their Firestore streams; a superseded bundle (the route's
  // tripId changed, the user changed, or the repos seam was swapped) must unsubscribe or
  // its listeners keep firing into state nothing renders. Deliberately not an effect
  // *cleanup*: StrictMode's simulated remount runs cleanups without rebuilding the memo,
  // which would leave the live bundle disposed. Disposing only the previous one is
  // remount-safe. `detail` is excluded — it owns its streams through open()/close() above.
  const previous = useRef<TripStores | null>(null);
  useEffect(() => {
    const superseded = previous.current;
    previous.current = stores;
    if (superseded !== null && superseded !== stores) {
      superseded.items.getState().dispose();
      superseded.categories.getState().dispose();
      superseded.todos.getState().dispose();
      superseded.notes.getState().dispose();
    }
  }, [stores]);

  return stores;
};
